import { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import {
  Clock,
  DollarSign,
  Plus,
  Edit,
  Trash2,
  Calendar,
  Building2,
  RefreshCw,
  CreditCard,
  AlertCircle,
  CheckCircle,
  Loader2,
  TrendingUp,
  BarChart3,
  PiggyBank,
  Link2
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Activity code colors
const activityColors = {
  research: 'bg-blue-100 text-blue-700',
  drafting: 'bg-purple-100 text-purple-700',
  court: 'bg-red-100 text-red-700',
  communication: 'bg-green-100 text-green-700',
  negotiation: 'bg-amber-100 text-amber-700',
  admin: 'bg-slate-100 text-slate-700'
};

export default function CaseBillingTab({ caseId, caseData, onUpdate }) {
  const { token } = useAuth();

  // State
  const [timeEntries, setTimeEntries] = useState([]);
  const [billingSummary, setBillingSummary] = useState(null);
  const [activityCodes, setActivityCodes] = useState([]);
  const [loading, setLoading] = useState(true);

  // Time entry dialog
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [newEntry, setNewEntry] = useState({
    date: new Date().toISOString().split('T')[0],
    hours: '',
    description: '',
    entry_type: 'billable',
    activity_code: ''
  });
  const [saving, setSaving] = useState(false);

  // Fetch data
  useEffect(() => {
    fetchTimeEntries();
    fetchBillingSummary();
    fetchActivityCodes();
  }, [caseId, token]);

  const fetchTimeEntries = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/time-entries`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setTimeEntries(data.entries || []);
      }
    } catch (error) {
      console.error('Error fetching time entries:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchBillingSummary = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/billing-summary`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setBillingSummary(data);
      }
    } catch (error) {
      console.error('Error fetching billing summary:', error);
    }
  };

  const fetchActivityCodes = async () => {
    try {
      const res = await fetch(`${API_URL}/api/case-management/activity-codes`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setActivityCodes(data);
      }
    } catch (error) {
      console.error('Error fetching activity codes:', error);
    }
  };

  const saveTimeEntry = async () => {
    if (!newEntry.hours || !newEntry.description) {
      toast.error('Please enter hours and description');
      return;
    }

    setSaving(true);
    try {
      const url = editingEntry
        ? `${API_URL}/api/case-management/cases/${caseId}/time-entries/${editingEntry.id}`
        : `${API_URL}/api/case-management/cases/${caseId}/time-entries`;

      const method = editingEntry ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...newEntry,
          hours: parseFloat(newEntry.hours),
          case_id: caseId
        })
      });

      if (res.ok) {
        toast.success(editingEntry ? 'Time entry updated' : 'Time entry created');
        setShowTimeDialog(false);
        setEditingEntry(null);
        setNewEntry({
          date: new Date().toISOString().split('T')[0],
          hours: '',
          description: '',
          entry_type: 'billable',
          activity_code: ''
        });
        fetchTimeEntries();
        fetchBillingSummary();
        if (onUpdate) onUpdate();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to save time entry');
      }
    } catch (error) {
      toast.error('Failed to save time entry');
    } finally {
      setSaving(false);
    }
  };

  const deleteTimeEntry = async (entryId) => {
    if (!confirm('Are you sure you want to delete this time entry?')) return;

    try {
      const res = await fetch(`${API_URL}/api/case-management/cases/${caseId}/time-entries/${entryId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Time entry deleted');
        fetchTimeEntries();
        fetchBillingSummary();
        if (onUpdate) onUpdate();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to delete time entry');
      }
    } catch (error) {
      toast.error('Failed to delete time entry');
    }
  };

  const getActivityLabel = (code) => {
    const activity = activityCodes.find(a => a.code === code);
    return activity?.description || code || 'Other';
  };

  const getActivityCategory = (code) => {
    const activity = activityCodes.find(a => a.code === code);
    return activity?.category || 'admin';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin mr-2" />
        <span>Loading billing data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Billing Summary Cards */}
      <div className="grid md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Hours</p>
                <p className="text-2xl font-bold">{billingSummary?.totals?.total_hours || 0}</p>
              </div>
              <Clock className="w-8 h-8 text-blue-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Billable Hours</p>
                <p className="text-2xl font-bold text-emerald-600">{billingSummary?.totals?.billable_hours || 0}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Unbilled Amount</p>
                <p className="text-2xl font-bold text-amber-600">
                  ${(billingSummary?.totals?.unbilled_amount || 0).toLocaleString()}
                </p>
              </div>
              <AlertCircle className="w-8 h-8 text-amber-500 opacity-50" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Collected</p>
                <p className="text-2xl font-bold text-emerald-600">
                  ${(billingSummary?.totals?.total_collected || 0).toLocaleString()}
                </p>
              </div>
              <CheckCircle className="w-8 h-8 text-emerald-500 opacity-50" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Fee Info */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="capitalize">
                {caseData?.fee_type?.replace('_', ' ') || 'Hourly'}
              </Badge>
              {caseData?.hourly_rate && (
                <span className="text-sm text-muted-foreground">
                  Rate: <strong>${caseData.hourly_rate}/hr</strong>
                </span>
              )}
            </div>
            <Button onClick={() => setShowTimeDialog(true)}>
              <Plus className="w-4 h-4 mr-2" />
              Log Time
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Time Entries */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Time Entries</CardTitle>
            <Badge variant="secondary">{timeEntries.length} entries</Badge>
          </div>
        </CardHeader>
        <CardContent>
          {timeEntries.length > 0 ? (
            <div className="space-y-2">
              {timeEntries.map((entry) => (
                <div 
                  key={entry.id}
                  className="flex items-center justify-between p-3 rounded-lg bg-slate-50 hover:bg-slate-100 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <div className="text-center min-w-[60px]">
                      <p className="text-2xl font-bold">{entry.hours}</p>
                      <p className="text-xs text-muted-foreground">hours</p>
                    </div>
                    <div className="h-10 w-px bg-border" />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{entry.description}</p>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge 
                          variant="outline" 
                          className={`text-xs ${activityColors[getActivityCategory(entry.activity_code)]}`}
                        >
                          {getActivityLabel(entry.activity_code)}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(entry.date).toLocaleDateString()}
                        </span>
                        <Badge 
                          variant={entry.entry_type === 'billable' ? 'default' : 'secondary'}
                          className="text-xs"
                        >
                          {entry.entry_type}
                        </Badge>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {entry.entry_type === 'billable' && (
                      <span className="font-semibold text-emerald-600">
                        ${(entry.amount || 0).toLocaleString()}
                      </span>
                    )}
                    {entry.is_billed ? (
                      <Badge className="bg-emerald-100 text-emerald-700">Billed</Badge>
                    ) : (
                      <div className="flex items-center gap-1">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => {
                            setEditingEntry(entry);
                            setNewEntry({
                              date: entry.date,
                              hours: entry.hours.toString(),
                              description: entry.description,
                              entry_type: entry.entry_type,
                              activity_code: entry.activity_code || ''
                            });
                            setShowTimeDialog(true);
                          }}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => deleteTimeEntry(entry.id)}
                        >
                          <Trash2 className="w-4 h-4 text-red-500" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Clock className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-4">No time entries yet</p>
              <Button onClick={() => setShowTimeDialog(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Log Your First Entry
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Activity Breakdown */}
      {billingSummary?.by_activity && Object.keys(billingSummary.by_activity).length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Time by Activity
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(billingSummary.by_activity).map(([code, data]) => (
                <div key={code} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Badge 
                      variant="outline" 
                      className={activityColors[getActivityCategory(code)]}
                    >
                      {code}
                    </Badge>
                    <span className="text-sm">{getActivityLabel(code)}</span>
                  </div>
                  <div className="flex items-center gap-4 text-sm">
                    <span>{data.hours} hrs</span>
                    <span className="font-semibold">${data.amount.toLocaleString()}</span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Time Entry Dialog */}
      <Dialog open={showTimeDialog} onOpenChange={setShowTimeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Clock className="w-5 h-5 text-blue-600" />
              {editingEntry ? 'Edit Time Entry' : 'Log Time Entry'}
            </DialogTitle>
            <DialogDescription>
              Record time spent on this case
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date</Label>
                <Input
                  type="date"
                  value={newEntry.date}
                  onChange={(e) => setNewEntry({ ...newEntry, date: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Hours</Label>
                <Input
                  type="number"
                  step="0.25"
                  min="0"
                  placeholder="1.5"
                  value={newEntry.hours}
                  onChange={(e) => setNewEntry({ ...newEntry, hours: e.target.value })}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Activity Code</Label>
              <Select
                value={newEntry.activity_code}
                onValueChange={(value) => setNewEntry({ ...newEntry, activity_code: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select activity" />
                </SelectTrigger>
                <SelectContent>
                  {activityCodes.map((activity) => (
                    <SelectItem key={activity.code} value={activity.code}>
                      {activity.code} - {activity.description}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Entry Type</Label>
              <Select
                value={newEntry.entry_type}
                onValueChange={(value) => setNewEntry({ ...newEntry, entry_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="billable">Billable</SelectItem>
                  <SelectItem value="non_billable">Non-Billable</SelectItem>
                  <SelectItem value="no_charge">No Charge</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                placeholder="Describe the work performed..."
                rows={3}
                value={newEntry.description}
                onChange={(e) => setNewEntry({ ...newEntry, description: e.target.value })}
              />
            </div>

            {newEntry.entry_type === 'billable' && newEntry.hours && caseData?.hourly_rate && (
              <div className="p-3 bg-emerald-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-emerald-700">Calculated Amount</span>
                  <span className="font-semibold text-emerald-700">
                    ${(parseFloat(newEntry.hours) * caseData.hourly_rate).toFixed(2)}
                  </span>
                </div>
                <p className="text-xs text-emerald-600 mt-1">
                  {newEntry.hours} hrs × ${caseData.hourly_rate}/hr
                </p>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowTimeDialog(false);
              setEditingEntry(null);
              setNewEntry({
                date: new Date().toISOString().split('T')[0],
                hours: '',
                description: '',
                entry_type: 'billable',
                activity_code: ''
              });
            }}>
              Cancel
            </Button>
            <Button onClick={saveTimeEntry} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {editingEntry ? 'Update Entry' : 'Log Time'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
